import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Toolbar,
  Typography,
  TextField,
  InputAdornment, Button,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Badge,
  IconButton,
  Tooltip, Autocomplete,
  CircularProgress,
  Pagination,
  Dialog,
  Snackbar,
  Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import "./TruInk.css";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";
import { ACCENT, ALL_PER_PAGE, GRADIENT, PLUGINS_PER_PAGE, SORT_OPTS, TEMPLATE_AVATAR_GRAD, TPLS_PER_PAGE, TYPE_OPTS } from "./InhubUtils/constants";
import { applyFilters, mapInkHubItem } from "./InhubUtils/helpers";
import PluginCard from "./InhubUtils/PluginCard";
import TemplateCard from "./InhubUtils/TemplateCard";
import ItemModal from "./InhubUtils/ItemModel";
import EmptyState from "./InhubUtils/EmptyState";
import BundleModal from "./InhubUtils/BundleModel";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { fetchCanvasSizes } from "../../../utils/truInkTemplate";

const BUNDLE_STORAGE_KEY = "truink_inkhub_bundle";

const loadStoredBundle = () => {
  try {
    const saved = localStorage.getItem(BUNDLE_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function InkStoreDashboard() {
  const [search,       setSearch]       = useState("");
  const [inputValue,   setInputValue]   = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [sortBy,       setSortBy]       = useState("Featured");
  const [bundle,       setBundle]       = useState(loadStoredBundle);
  const [purchased,    setPurchased]    = useState([]);
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modalItem,    setModalItem]    = useState(null);
  const [pluginPage,   setPluginPage]   = useState(1);
  const [tplPage,      setTplPage]      = useState(1);
  const [allPage,      setAllPage]      = useState(1);
  const [toast,        setToast]        = useState({ open: false, message: "", severity: "success" });
  const [bundleOpen,   setBundleOpen]   = useState(false);
  const theme = useTheme();
  const isModalActive = useMemo(() => !!modalItem, [modalItem]);
  const tenantId = useSelector((state) => state.truInk?.selectedSite?.tenantId);

  useEffect(() => {
    // tenantId arrives from the persisted truInk slice and can flip from
    // undefined to a value just after mount, running this twice. Without the
    // flag the slower of the two responses wins, whichever it is.
    let current = true;

    const fetchItems = async () => {
      setLoading(true);

      // Canvas sizes are what turn an item's canvasSizeId into the resolution
      // its elements were authored against -- without them every preview is
      // scaled against a fallback and comes out tiny.
      const [data, canvasSizes] = await Promise.all([
        callPiggybackAPI("inkHub", {}, {}, "GET"),
        tenantId
          ? fetchCanvasSizes(tenantId).catch((error) => {
              console.error("Error fetching canvas sizes for InkHub:", error);
              return [];
            })
          : Promise.resolve([]),
      ]);

      const canvasSizesById = (Array.isArray(canvasSizes) ? canvasSizes : []).reduce(
        (acc, size) => {
          if (size?.id && size?.resolution) acc[size.id] = size.resolution;
          return acc;
        },
        {}
      );

      if (!current) return;

      if (Array.isArray(data)) {
        setItems(data.map((raw) => mapInkHubItem(raw, canvasSizesById)));
      }
      setLoading(false);
    };
    fetchItems();

    return () => {
      current = false;
    };
  }, [tenantId]);

  useEffect(() => { setPluginPage(1); setTplPage(1); setAllPage(1); }, [search, selectedType, sortBy]);

  useEffect(() => {
    try {
      localStorage.setItem(BUNDLE_STORAGE_KEY, JSON.stringify(bundle));
    } catch {
      // ignore write failures (e.g. storage full or disabled)
    }
  }, [bundle]);

  const allSuggestions = useMemo(
    () => [
      ...new Set([
        ...items.map((i) => i.category),
        ...items.flatMap((i) => i.tags),
        ...items.map((i) => i.name),
      ]),
    ],
    [items]
  );

  const toggleBundle = (id) =>
    setBundle((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const handleGet = (item) =>
    setPurchased((p) => [...new Set([...p, item.id])]);

  const handleBuyTemplate = useCallback((item) => {
    setBundle((p) => [...new Set([...p, item.id])]);
    setBundleOpen(true);
  }, []);

  const handleSwitchTemplate = useCallback((newItem, pairIds) => {
    if (pairIds.length > 0) {
      setBundle((p) => [...new Set([...p, ...pairIds])]);
      setToast({ open: true, message: `${pairIds.length} template${pairIds.length > 1 ? "s" : ""} added to bundle`, severity: "success" });
    }
    setModalItem(newItem);
  }, []);
  const handleBundleAll = useCallback((ids) => {
    setBundle((p) => [...new Set([...p, ...ids])]);
    setModalItem(null);
    setBundleOpen(true);
  }, []);

  const handleCheckoutSuccess = useCallback((siteObj, checkedOutItems) => {
    const count = checkedOutItems?.length ?? 0;
    const itemLabel = count > 1 ? "items are" : "item is";
    const siteLabel = siteObj?.name ? ` on ${siteObj.name}` : "";
    setToast({
      open: true,
      message: `Your ${itemLabel} live${siteLabel}! Visit the site to explore it.`,
      severity: "success",
    });
  }, []);

  const allPlugins = useMemo(
    () => applyFilters(items.filter((i) => i.type === "Plugin"), search, sortBy),
    [items, search, sortBy]
  );
  const allTemplates = useMemo(
    () => applyFilters(items.filter((i) => i.type === "Template"), search, sortBy),
    [items, search, sortBy]
  );

  const displayPlugins   = useMemo(() => selectedType === "Template" ? [] : allPlugins,   [selectedType, allPlugins]);
  const displayTemplates = useMemo(() => selectedType === "Plugin"   ? [] : allTemplates, [selectedType, allTemplates]);

  const pluginPageCount = Math.ceil(displayPlugins.length   / PLUGINS_PER_PAGE);
  const tplPageCount    = Math.ceil(displayTemplates.length / TPLS_PER_PAGE);
  const pagedPlugins    = displayPlugins.slice((pluginPage - 1) * PLUGINS_PER_PAGE, pluginPage * PLUGINS_PER_PAGE);
  const pagedTemplates  = displayTemplates.slice((tplPage - 1) * TPLS_PER_PAGE, tplPage * TPLS_PER_PAGE);

  const displayAll   = useMemo(
    () => selectedType === "All" ? applyFilters(items, search, sortBy) : [],
    [selectedType, items, search, sortBy]
  );
  const allPageCount = Math.ceil(displayAll.length / ALL_PER_PAGE);
  const pagedAll     = displayAll.slice((allPage - 1) * ALL_PER_PAGE, allPage * ALL_PER_PAGE);

  const pillSx = (active) => ({
    textTransform: "none",
    minWidth: "unset",
    width: "fit-content",
    borderRadius: "10px",
    minHeight: 28,
    height: 28,
    fontSize: "0.78rem",
    px: 1.5,
    lineHeight: 1,
    ...(active
      ? { color: "#fff", background: GRADIENT, "&:hover": { background: GRADIENT, opacity: 0.92 } }
      : { color: "#555", border: "1px solid #ccc", backgroundColor: "transparent", "&:hover": { backgroundColor: "#f5f5f5" } }),
  });

  const pluginCardProps   = { bundle, purchased, toggleBundle, handleGet: handleBuyTemplate };
  const templateCardProps = { bundle, purchased, toggleBundle, handleGet: handleBuyTemplate };


  return (
    <Box sx={{ marginLeft: {xs:'0px',md:"270px"} }}>
      <Toolbar />
      <Box sx={{ position: "relative", overflow: "hidden", minHeight: "calc(100vh - 64px)", backgroundColor: "#ffffff" }}>
        <div className="wave1" />
        <div className="wave2" />
        <div className="wave3" />
        <div className="wave4" />

        <Box sx={{ position: "relative", zIndex: 1, p: 3 }}>
          <Box sx={{ mb: {xs:1,md:3} }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: {xs:1.5,md:2.5} }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShoppingBagOutlinedIcon sx={{ color: ACCENT, fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: ACCENT, letterSpacing: "-0.3px" }}>
                  inkHub
                </Typography>
                <Typography variant="body2" sx={{ color: "#888", ml: 1, fontSize: "0.82rem" ,textAlign:{xs:"start",md:"unset"} }}>
                  Premium templates & plugins for truInk
                </Typography>
              </Box>
              <Tooltip title={`Bundle (${bundle.length})`}>
                <Badge badgeContent={bundle.length} color="error">
                  <IconButton size="small" onClick={() => setBundleOpen(true)} sx={{ color: "#555" }}>
                    <ShoppingCartIcon fontSize="small" />
                  </IconButton>
                </Badge>
              </Tooltip>
            </Box>
            <Box
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                overflow: "hidden",
                background: TEMPLATE_AVATAR_GRAD,
                px:  {xs:2,sm:2,md:3.5},
                py: {xs:1,sm:1.5,md:5.5},
                display: "flex",
                alignItems: "center",
                flexDirection:{xs:"column",md:"row"},
                justifyContent: "space-between",
                boxShadow: "0 4px 24px rgba(90,28,47,0.18)",
                gap:{xs:2,md:"unset"}
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", lineHeight: 1.2, mb: 0.4 }}>
                  Premium templates &amp; plugins
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.84rem" ,textAlign:{xs:"start",md:"unset"}}}>
                  Built for truInk digital signage — install in one click, live on your displays instantly.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexShrink: 0, ml:{xs:0,md: 4} }}>
                {[
                  { value: items.filter((i) => i.type === "Template").length, label: "Templates" },
                  { value: items.filter((i) => i.type === "Plugin").length,   label: "Plugins"   },
                  { value: items.filter((i) => i.price === 0).length,         label: "Free"      },
                ].map(({ value, label }) => (
                  <Box key={label} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#fff", lineHeight: 1 }}>{value}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.65)", letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {TYPE_OPTS.map((t) => (
                <Button key={t} disableElevation onClick={() => setSelectedType(t)} sx={pillSx(selectedType === t)}>
                  {t}
                </Button>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Autocomplete
                freeSolo
                options={allSuggestions}
                inputValue={inputValue}
                onInputChange={(_, val) => { setInputValue(val); setSearch(val); }}
                onChange={(_, val) => { if (val) { setSearch(val); setInputValue(val); } }}
                filterOptions={(opts, { inputValue: q }) =>
                  q.trim().length < 1
                    ? []
                    : opts.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
                }
                sx={{ width: {xs:200,md:260} }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search templates, plugins, tags…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: "#aaa" }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <FormControl size="small" sx={{ minWidth: {xs:"unset",md:148}, ml: "auto" }}>
                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} displayEmpty sx={{ fontSize: "0.82rem" }}>
                  {SORT_OPTS.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontSize: "0.82rem" }}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : (
            <>
              {selectedType === "All" && (
                <>
                  <Typography sx={{ mb: 1.5, color: "#999", fontSize: "0.78rem" }}>
                    {displayAll.length} item{displayAll.length !== 1 ? "s" : ""}
                  </Typography>
                  {displayAll.length === 0 ? (
                    <EmptyState onClear={() => { setSearch(""); setInputValue(""); setSelectedType("All"); }} />
                  ) : (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2, mb: 2 }}>
                        {pagedAll.map((item) =>
                          item.type === "Plugin" ? (
                            <PluginCard key={item.id} item={item} onOpen={setModalItem} {...pluginCardProps} />
                          ) : (
                            <TemplateCard key={item.id} item={item} onOpen={setModalItem} {...templateCardProps} />
                          )
                        )}
                      </Box>
                      {allPageCount > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <Pagination
                            count={allPageCount}
                            page={allPage}
                            onChange={(_, v) => setAllPage(v)}
                            sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: ACCENT, color: "#fff" } }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
              {selectedType === "Plugin" && (
                <>
                  <Typography sx={{ mb: 1.5, color: "#999", fontSize: "0.78rem" }}>
                    {displayPlugins.length} {displayPlugins.length === 1 ? "result" : "results"}
                  </Typography>
                  {displayPlugins.length === 0 ? (
                    <EmptyState onClear={() => { setSearch(""); setInputValue(""); }} />
                  ) : (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2, mb: 2 }}>
                        {pagedPlugins.map((item) => (
                          <PluginCard key={item.id} item={item} onOpen={setModalItem} {...pluginCardProps} />
                        ))}
                      </Box>
                      {pluginPageCount > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <Pagination
                            count={pluginPageCount}
                            page={pluginPage}
                            onChange={(_, v) => setPluginPage(v)}
                            sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: ACCENT, color: "#fff" } }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
              {selectedType === "Template" && (
                <>
                  <Typography sx={{ mb: 1.5, color: "#999", fontSize: "0.78rem" }}>
                    {displayTemplates.length} {displayTemplates.length === 1 ? "result" : "results"}
                  </Typography>
                  {displayTemplates.length === 0 ? (
                    <EmptyState onClear={() => { setSearch(""); setInputValue(""); }} />
                  ) : (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2, mb: 2 }}>
                        {pagedTemplates.map((item) => (
                          <TemplateCard key={item.id} item={item} onOpen={setModalItem} {...templateCardProps} />
                        ))}
                      </Box>
                      {tplPageCount > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <Pagination
                            count={tplPageCount}
                            page={tplPage}
                            onChange={(_, v) => setTplPage(v)}
                            sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: ACCENT, color: "#fff" } }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
      <Dialog
        open={!!modalItem}
        onClose={() => setModalItem(null)}
        maxWidth={false}
        PaperProps={{
           elevation: 0, 
           sx: { 
            backgroundColor: "transparent", 
            overflow: "visible", 
            boxShadow: "none" ,
            ...(
            isModalActive &&{
              [theme.breakpoints.down("md")]: {width:"95%" ,maxWidth:"unset",height:"100%" ,justifyContent:"center",minWidth:"95%" }
            }
          )
          },
        }}
      >
        {modalItem && <ItemModal
          modalItem={modalItem}
          items={items}
          bundle={bundle}
          purchased={purchased}
          toggleBundle={toggleBundle}
          handleGet={handleGet}
          onClose={() => setModalItem(null)}
          onSwitchTemplate={handleSwitchTemplate}
          onBundleAll={handleBundleAll}
        />}
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity ?? "success"} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
          {toast.message}
        </Alert>
      </Snackbar>
      {bundleOpen && (
        <BundleModal
          bundle={bundle}
          items={items}
          purchased={purchased}
          handleGet={handleGet}
          toggleBundle={toggleBundle}
          onClose={() => setBundleOpen(false)}
          clearBundle={() => setBundle([])}
          onCheckoutSuccess={handleCheckoutSuccess}
        />
      )}
    </Box>
  );
}


